import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, PackageSearch, Calendar, Pencil, Eye, Trash2, Egg, Bird, Ham, Drumstick } from "lucide-react";
import { productsAPI } from "../api";
import Hint from "../components/Onboarding/Hint";
import { useAuth } from "../context/AuthContext";
import ConfirmModal from "../components/ConfirmModal";
import "./MyProducts.css";

const PRODUCT_TYPE_ICONS = {
  eggs: Egg,
  poultry: Bird,
  other: Ham,
  chicken: Drumstick,
};

// Fonction pour formater l'unité avec le pluriel correct
const formatUnit = (unit, quantity) => {
  if (quantity > 1) {
    if (unit === "poulet") return "poulets";
    if (unit === "pièce") return "pièces";
    if (unit === "lot") return "lots";
  }
  return unit;
};

const MyProducts = () => {
  const { isAuthenticated, isBreeder } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, productId: null, productName: "" });

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await productsAPI.getMyProducts();
      setProducts(response.data);
    } catch (err) {
      setError("Erreur lors du chargement de vos produits");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && isBreeder) {
      loadProducts();
    }
  }, [isAuthenticated, isBreeder, loadProducts]);

  const handleDeleteClick = (productId, productName) => {
    setDeleteModal({
      isOpen: true,
      productId,
      productName
    });
  };

  const handleDeleteConfirm = async () => {
    const { productId, productName } = deleteModal;
    setDeletingId(productId);
    setDeleteModal({ isOpen: false, productId: null, productName: "" });
    setError("");
    setSuccess("");
    
    try {
      await productsAPI.delete(productId);
      setSuccess(`Le produit "${productName}" a été supprimé avec succès.`);
      await loadProducts();
      // Effacer le message de succès après 3 secondes
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la suppression du produit. Veuillez réessayer.");
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, productId: null, productName: "" });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Immédiatement";
    const date = new Date(dateString);
    const now = new Date();
    if (date > now) {
      return `À partir du ${date.toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    }
    return "Disponible";
  };

  const isProductAvailable = (product) => {
    if (!product.is_available) return false;
    if (!product.available_from) return true;
    return new Date(product.available_from) <= new Date();
  };

  if (!isAuthenticated) {
    return (
      <div className="container main-content">
        <div className="alert alert-error">
          Vous devez être connecté pour voir vos produits
        </div>
        <Link to="/login" className="btn btn-primary">
          Se connecter
        </Link>
      </div>
    );
  }

  if (!isBreeder) {
    return (
      <div className="container main-content">
        <div className="alert alert-error">
          Cette page est réservée aux éleveurs
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container main-content">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container main-content">
      <div className="page-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
          <div>
            <h1>Mes produits</h1>
            <p>Gérez vos produits et leur disponibilité</p>
          </div>
          <Link to="/products/create" className="btn btn-primary" data-tour="my-products-add">
            <Plus size={16} strokeWidth={2.5} /> Ajouter un produit
          </Link>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {products.length > 0 && (
        <Hint id="my_products_management">
          Vos commandes reçues s'affichent dans l'onglet <strong>Commandes</strong>. Utilisez <strong>Modifier</strong> pour ajuster le prix ou le stock à tout moment.
        </Hint>
      )}

      {products.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><PackageSearch size={56} strokeWidth={1.75} /></div>
          <h3>Aucun produit</h3>
          <p>Vous n'avez pas encore créé de produit</p>
          <Link to="/products/create" className="btn btn-primary">
            Créer mon premier produit
          </Link>
        </div>
      ) : (
        <div className="my-products-grid">
          {products.map((product) => (
            <div key={product.id} className="my-product-card card">
              <div className="my-product-header">
                <div className="my-product-image">
                  {(() => {
                    const firstImage = product.images && product.images.length > 0 
                      ? product.images[0].image_url 
                      : product.image_url;
                    
                    return firstImage ? (
                      <img src={firstImage} alt={product.name} />
                    ) : (
                      <div className="product-image-placeholder">
                        {(() => {
                          const TypeIcon = PRODUCT_TYPE_ICONS[product.product_type] || Drumstick;
                          return <TypeIcon size={40} strokeWidth={1.5} color="var(--primary)" />;
                        })()}
                      </div>
                    );
                  })()}
                </div>
                <div className="my-product-status">
                  {isProductAvailable(product) ? (
                    <span className="badge badge-success">Disponible</span>
                  ) : (
                    <span className="badge badge-warning">Non disponible</span>
                  )}
                  {product.available_from && new Date(product.available_from) > new Date() && (
                    <span className="badge badge-info icon-inline" style={{ marginTop: "0.25rem", display: "block" }}>
                      <Calendar size={13} strokeWidth={2.25} /> {formatDate(product.available_from)}
                    </span>
                  )}
                </div>
              </div>

              <div className="my-product-info">
                <h3>{product.name}</h3>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
                  {product.product_type && (
                    <span className="badge badge-info" style={{ fontSize: "0.8rem" }}>
                      {product.product_type === "chicken" ? "🐔 Poulet" : 
                       product.product_type === "eggs" ? "🥚 Œufs" : 
                       product.product_type === "poultry" ? "🦆 Volaille" : 
                       "🐷 Autre"}
                    </span>
                  )}
                  {product.is_live && (
                    <span className="badge badge-success" style={{ fontSize: "0.8rem" }}>
                      🐔 Vivant
                    </span>
                  )}
                  {product.category && (
                    <span className="badge badge-primary">{product.category}</span>
                  )}
                </div>
                <p className="my-product-description">
                  {product.description || "Aucune description"}
                </p>
                <div className="my-product-details">
                  <div>
                    <strong>Prix:</strong>{" "}
                    {product.unit === "lot" && product.lot_price
                      ? `${product.lot_price.toLocaleString("fr-FR")} F CFA / lot`
                      : `${product.price.toLocaleString("fr-FR")} F CFA / ${product.unit}`}
                  </div>
                  <div>
                    <strong>Stock:</strong> {product.stock} {formatUnit(product.unit, product.stock)}
                    {product.unit === "lot" && product.lot_size && (
                      <span> ({product.lot_size} {product.lot_size > 1 ? "éléments" : "élément"}/lot)</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="my-product-actions">
                <Link
                  to={`/products/${product.id}/edit`}
                  className="btn btn-outline btn-sm"
                >
                  <Pencil size={14} strokeWidth={2.25} /> Modifier
                </Link>
                <Link
                  to={`/products/${product.id}`}
                  className="btn btn-outline btn-sm"
                >
                  <Eye size={14} strokeWidth={2.25} /> Voir
                </Link>
                <button
                  onClick={() => handleDeleteClick(product.id, product.name)}
                  className="btn btn-error btn-sm"
                  disabled={deletingId === product.id}
                >
                  {deletingId === product.id ? "..." : <><Trash2 size={14} strokeWidth={2.25} /> Supprimer</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Supprimer le produit"
        message={
          deleteModal.productName
            ? `Êtes-vous sûr de vouloir supprimer "${deleteModal.productName}" ? Cette action est irréversible et toutes les données associées seront perdues.`
            : "Êtes-vous sûr de vouloir supprimer ce produit ? Cette action est irréversible."
        }
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default MyProducts;

